// ./src/hooks/useEvent.ts
//
// Loads a single event by id for the event dashboard / detail pages. Reloads when
// the id changes; race-guarded. `setEvent` lets callers patch the local copy after
// a mutation (e.g. publish) without a full refetch.

import { useCallback, useEffect, useRef, useState } from "react";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";

import { EventService, Event } from "@/utils/api";

export interface UseEventReturn {
  event: Event | null;
  loading: boolean;
  error: OpResult | null;
  reload: () => Promise<void>;
  setEvent: React.Dispatch<React.SetStateAction<Event | null>>;
}

export const useEvent = (id?: string | null): UseEventReturn => {
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<OpResult | null>(null);
  const callIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!id) {
      setEvent(null);
      return;
    }
    const myCall = ++callIdRef.current;
    setLoading(true);
    setError(null);

    const result = await EventService.get(id);

    if (myCall !== callIdRef.current) {
      return;
    }
    setLoading(false);

    if (result.code === OP_RESULT_CODES.OK && result.data) {
      const rows = Array.isArray(result.data) ? (result.data as Event[]) : [];
      setEvent(rows[0] ?? null);
    } else {
      setError(result);
      setEvent(null);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  return { event, loading, error, reload: load, setEvent };
};
